import React, {ReactElement, useState} from 'react';
import AccordionItem from './AccordionItem';
import {AccordionItemProps} from '../../../types';

import './Accordion.scss';

export enum AccordionStartOpen {
  ALL,
  FIRST,
  LAST,
}

interface Props {
  items: AccordionItemProps[];
  isMultiple?: boolean;
  startOpen?: AccordionStartOpen;
  className?: string;
}

function Accordion(props: Props): ReactElement {
  const {items, isMultiple, startOpen, className} = props;
  const itemsKeys = Object.keys(items);
  // Return the items that should be open depending on `startOpen` prop: 'all', 'first' or none (anything else, really) passed.
  const startingOpenItems: string[] = itemsKeys.filter((entry, i) => {
    const isStartOpen =
      startOpen === AccordionStartOpen.ALL ||
      (startOpen === AccordionStartOpen.FIRST && i === 0) ||
      (startOpen === AccordionStartOpen.LAST && i === itemsKeys.length - 1);
    return isStartOpen;
  });
  // Applies the list of open items.
  const [openItems, setOpenItems] = useState<string[]>(startingOpenItems);
  /**
   * This function adds or removes the child identifier key from the state to be later compared to define if an item is open or closed.
   * If the item's key is in the list it means it's open (not collapsed).
   */
  const handleItemCollapsedState = (itemKey: string) => {
    // If the item is in the array (meaning it's open), remove it -> closing it
    if (openItems.includes(itemKey)) {
      setOpenItems(openItems.filter((entry) => entry !== itemKey));
    } else {
      // Opens the thing (adding to the array - if isMultiple)
      // or replacing everything in the array with the item's key (only 1 item opened accepeted)
      setOpenItems([...(isMultiple ? openItems : []), itemKey]);
    }
  };

  return (
    <div className={`accordion ${className || ''}`.trim()}>
      {items.map((item, i) => {
        const key = itemsKeys[i];
        return (
          <AccordionItem
            key={key}
            isOpen={openItems.includes(key)}
            handleItemCollapsedState={() => handleItemCollapsedState(key)}
            label={item.label}
            id={item.id}
          >
            {item.children}
          </AccordionItem>
        );
      })}
    </div>
  );
}

export default Accordion;
